import { Flex, Modal, Typography } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import type { UserRoles } from "~enums/userRoles";
import type { UserPermissionTransformed } from "../../../types";
import { useGetUserPermissionScope } from "../../hooks";
import { isTeamManagerProductRole } from "../../utilities";
import { PermissionNameList } from "..";
import ScopeView from "./ScopeView";
import type { ScopeOption } from "./types/scopeOption";
import extractScopeData from "./utilities/extractScopeData";

import "./ViewUserPermissionsModal.scss";

type Props = {
  userPermission: UserPermissionTransformed | null;
  isOpen: boolean;
  onClose: () => void;
};

export const ViewUserPermissionModal = ({ userPermission, isOpen, onClose }: Props) => {
  const { formatMessage } = useIntl();
  const shouldFetchScope = !!userPermission?.scope;

  const { data: scopeData, isLoading: isScopeLoading } = useGetUserPermissionScope(userPermission?.permissionId, shouldFetchScope);

  if (!userPermission) {
    return null;
  }

  const showAdvancedSection = !isTeamManagerProductRole(userPermission.userRole as UserRoles);
  let scopeOptions: ScopeOption[] = [];
  if (scopeData && userPermission.scope) {
    scopeOptions = extractScopeData(
      scopeData.scope?.leagueTree,
      scopeData.scope?.entityGroupTree,
      scopeData?.resources || {},
      userPermission.scope,
    );
  }

  const userRoleName = formatMessage({ id: `permissions.userRole.${userPermission.userRole}` });
  const scopeName = userPermission.scope ? formatMessage({ id: userPermission.scope.toLowerCase() }) : "";

  let title = userRoleName;
  if (scopeName) {
    title += ` - ${scopeName}`;
  }

  const { seasonStatuses = null, fixtureStatuses = null } = userPermission;

  return (
    <Modal
      className="permissions-modal permissions-modal-view-user-permission"
      open={isOpen}
      width={750}
      centered
      onCancel={onClose}
      title={formatMessage({ id: "permissions.user.role.viewAccess" })}
      cancelText={formatMessage({ id: "close" })}
      okButtonProps={{ hidden: true }}
      destroyOnHidden
    >
      <Flex justify="space-between" align="center">
        <Typography.Text className="section-header">{title}</Typography.Text>
        {userPermission.scope && scopeOptions.length > 0 && (
          <Typography.Text className="items-selected-text">
            <FormattedMessage
              id="items.selected"
              values={{
                number: scopeOptions.length,
                itemType: formatMessage({
                  id: scopeOptions.length > 1 ? userPermission.scope.toLowerCase() : userPermission.scope.slice(0, -1).toLowerCase(),
                }),
              }}
            />
          </Typography.Text>
        )}
      </Flex>
      <Flex gap={8} vertical>
        <ScopeView
          scopeType={userPermission.scope}
          options={scopeOptions}
          isScopeLoading={isScopeLoading}
          key={userPermission.permissionId}
        />
        {showAdvancedSection && (
          <>
            <div>
              <Typography.Text className="section-header">{formatMessage({ id: "season.statuses" })}</Typography.Text>
              <br />
              <PermissionNameList value={seasonStatuses} />
            </div>
            <div>
              <Typography.Text className="section-header">{formatMessage({ id: "fixture.statuses" })}</Typography.Text>
              <br />
              <PermissionNameList value={fixtureStatuses} />
            </div>
          </>
        )}
      </Flex>
    </Modal>
  );
};
